"use client"

import { useRouter } from "next/router"

import { useTransportOrganizationContext } from "@/transport"
import { useOrganization } from "@clerk/nextjs"

import { useQueryParams } from "@/hooks/useQueryParams"

import { IncidentStatusBanner } from "../components/IncidentStatusBanner"
import { getSettingsSectionPath } from "../config/settingsSections"
import { useSystemStatusTransport } from "./useSystemStatusTransport"

interface IncidentStatusBannerRouteProps {
  className?: string
}

// The Pages Router route pattern behind getSettingsSectionPath("system-status").
const SYSTEM_STATUS_PAGE_PATH = "/[slug]/settings/system-status"

/**
 * The platform-incident banner wherever the customer is, rather than only in
 * Settings. Owns its own query so the shell does not have to know about system
 * status: the app shell renders whatever the navigation module contributes
 * here, and the query is shared through React Query with the system-status
 * settings page, so mounting both costs one request, not two.
 *
 * The status payload is organization-independent, so the poll itself waits
 * for nothing. The dismissal scope and the link target are not: both follow
 * this tab's organization, so the banner waits for the route and Clerk's
 * active organization to agree — the same gate BillingStatusBannerRoute
 * applies — rather than scoping either against whichever organization the
 * active session names while this tab's route says another one. While they
 * disagree — which every organization switch passes through, since Clerk's
 * active organization and the route resolve independently — the banner stays
 * mounted with its scoped props nulled, rendering nothing: unmounting would
 * fire its episode cleanup and drop the previous organization's stored
 * dismissal.
 */
export const IncidentStatusBannerRoute = ({
  className,
}: IncidentStatusBannerRouteProps) => {
  const router = useRouter()
  const { organization } = useOrganization()
  const { isAuthLoaded, isOrganizationSynced, organizationSlug } =
    useTransportOrganizationContext()
  const queryParams = useQueryParams()
  const systemStatus = useSystemStatusTransport()

  // The system-status page already shows the full incident picture, and this
  // banner's link would lead back to the page it sits on.
  if (router.pathname === SYSTEM_STATUS_PAGE_PATH) {
    return null
  }

  // The dismissal key and the "View system status" href are organization-
  // scoped, so they must follow the organization this tab's route names —
  // never the active organization alone, which can name another tab's
  // organization until route-organization sync catches up.
  const isOrganizationInScope =
    organization != null &&
    isAuthLoaded &&
    isOrganizationSynced &&
    organizationSlug !== null &&
    organization.slug === organizationSlug

  // Null the scoped props instead of unmounting while the route and Clerk's
  // active organization disagree: the banner renders nothing either way, but
  // unmounting would end its episode and drop the previous organization's
  // stored dismissal — the outcome the banner's split-effect design exists to
  // prevent.
  const scopedOrganization = isOrganizationInScope ? organization : null

  // A failed background refetch still leaves the last payload in `data`; fail
  // closed on transport errors so the banner never raises a cached incident as
  // current, matching the settings panel's sourceIsCurrent treatment.
  return (
    <IncidentStatusBanner
      className={className}
      systemStatus={
        scopedOrganization !== null && !systemStatus.error
          ? (systemStatus.data ?? null)
          : null
      }
      organizationId={scopedOrganization?.id ?? null}
      statusHref={queryParams.getHref(
        getSettingsSectionPath("system-status"),
        organizationSlug ?? undefined,
      )}
    />
  )
}
